import { ApiError, apiFetch } from './http';

export interface ServerStatus {
  alive: boolean;
  startedAt: string | null;
  playersOnline: number;
  maxPlayers: number;
  version: string | null;
  map: string | null;
  description: string | null;
  headlessClientsCount: number;
}

export type Access = 'MANAGED' | 'LINKED' | 'NONE';
export type ServerAction =
  'start' | 'stop' | 'restart' | 'mission' | 'configure' | 'delete' | 'link' | 'unlink';

/** What a mission that says «per defecte del servidor» gets for respawn on this server. */
export interface RespawnDefault {
  mode: 'none' | 'base' | 'leader' | 'lives' | 'waves';
  delay: number;
  lives: number;
  shared: boolean;
  waveMinutes: number;
}

export interface Lifecycle {
  headlessClients: number;
  startBeforeMinutes: number;
  stopIdleMinutes: number;
  keepForCampaign: boolean;
  retentionDays: number;
  respawn?: RespawnDefault;
}

export function saveServerRespawn(id: number, body: RespawnDefault): Promise<RespawnDefault> {
  return apiFetch<RespawnDefault>(`/api/servers/${id}/respawn`, { method: 'PUT', body });
}

export interface ServerView {
  id: number;
  name: string;
  description: string | null;
  type: string;
  port: number;
  maxPlayers: number;
  battlEye: boolean;
  missionTemplate: string | null;
  /**
   * What a person reads for `missionTemplate`, which is a folder name: the
   * title of the platform's mission behind `ops_<code>`, the panel's own name
   * for anybody else's. Absent from older platforms.
   */
  missionTitle?: string | null;
  /** The Workshop mods it runs, in the panel's order; `id` is null for a mod with no numeric Workshop id. */
  activeMods: { id: number | null; name: string }[];
  status: ServerStatus;
  access: Access;
  actions: ServerAction[];
  lifecycle: Lifecycle | null;
  /** True when the server's active mods carry the platform's addon (@ops). Absent on older backends. */
  opsMod?: boolean;
  /**
   * The identity of the server's active mods, hashed the way a preset's are.
   * The panel does not record which preset a server was built from, so this is
   * what says a server can run a mission generated for a preset: same hash,
   * same mods. Absent on older backends.
   */
  modSetHash?: string | null;
  /** The Creator DLCs the server runs, by name: half of its mod set with the Workshop mods. */
  dlcs?: string[];
  /** Whether the platform has a catalogue of what the server runs now; without one no mission can be made for it. */
  scanned?: boolean;
  /** The eras a night on it can be set in and the one proposed; a suggestion until somebody confirms it. */
  setting?: ServerSetting;
  /**
   * The panel's own record of who made the server: its service account's name,
   * or null for the operator's. Null too on a panel that does not say (older
   * than its service account); `panelLinkable` is then null as well.
   */
  panelManagedBy?: string | null;
  /** Whether the operator lets the panel's service account start, stop and switch this server's mission; null when the panel does not say. */
  panelLinkable?: boolean | null;
  /**
   * What the server's configuration says of the helicopters' flight model:
   * forced standard, forced advanced, or each player's own option (the
   * engine's default when the configuration says nothing). Null for a value
   * the engine does not have; absent on older backends.
   */
  flightModel?: FlightModel | null;
}

export type FlightModel = 'standard' | 'advanced' | 'player';

/**
 * What forcing the standard flight model did to one server: `changed`,
 * `already` (it said so before) or `failed`, with the panel's own words in
 * `detail` (a running server, which the panel will not modify, is the usual
 * one). Nothing is restarted: a changed server flies standard from its next
 * start.
 */
export interface FlightModelResult {
  id: number;
  name: string;
  outcome: 'changed' | 'already' | 'failed';
  was: string | null;
  now: string | null;
  detail: string | null;
}

/** Forces the helicopters' standard flight model on every Arma 3 server the panel lists (admins). */
export function forceStandardFlightEverywhere(): Promise<FlightModelResult[]> {
  return apiFetch<FlightModelResult[]>('/api/servers/flight-model', { method: 'POST' });
}

/**
 * A server's setting (docs/design/SERVER_SETTING_STUDY.md): `eras` is what the
 * quick start offers for a mission made for it, `defaultEra` first. `confirmed`
 * false is the platform's own reading of the server's mods, with `because`
 * naming the mods and DLCs it read it from.
 */
export interface ServerSetting {
  defaultEra: string;
  eras: string[];
  confirmed: boolean;
  because: string[];
}

export function saveServerSetting(
  id: number,
  body: { defaultEra: string; eras: string[] },
): Promise<ServerSetting> {
  return apiFetch<ServerSetting>(`/api/setting/servers/${id}`, { method: 'PUT', body });
}

/** The platform's addon on the panel: what this build bundles and what the panel has installed. */
export interface AddonState {
  bundled: boolean;
  bundledVersion: string | null;
  installed: boolean;
  installedVersion: string | null;
}

export interface Budget {
  max: number;
  used: number;
}

export interface ServersResponse {
  panelConfigured: boolean;
  panelReachable: boolean;
  lastSuccessSecondsAgo: number | null;
  lastFailure: string | null;
  budget: Budget;
  servers: ServerView[];
  /** Absent until the backend ships the addon endpoint. */
  addon?: AddonState;
  /** The server new ones copy their settings from, when an admin named one. */
  referenceServerId?: number | null;
}

/**
 * Where a new server's mods come from. Never a panel preset (2026-09-19): on the
 * owner's panel those are Launcher files imported once, and the mods that count
 * are what each server runs. One of: another server, whose mods and Creator DLCs
 * are copied as they are now; a list of Workshop ids, which is how a server is
 * made for a mission's own mods; or the text of a Launcher preset file, read
 * for its list and kept nowhere.
 */
export interface ModSource {
  fromServerId?: number;
  ids?: number[];
  launcherPreset?: string;
}

export interface ServerRequest {
  name: string;
  description?: string;
  mods?: ModSource | null;
  /** Creator DLCs by the panel's short id; absent is the source server's own, or the reference server's. */
  activeDlcs?: string[];
  maxPlayers?: number;
  password?: string;
  battlEye?: boolean;
  headlessClients?: number;
  missionTemplate?: string;
  startBeforeMinutes?: number;
  stopIdleMinutes?: number;
  keepForCampaign?: boolean;
  retentionDays?: number;
}

/** The servers page's own list: every server with how it is run and what may be done to it. A server manager's. */
export function fetchServers(): Promise<ServersResponse> {
  return apiFetch<ServersResponse>('/api/servers');
}

/**
 * A server as somebody choosing where a night is played needs it: its name,
 * whether it is up, whether the platform may start a session on it (`access`),
 * the mod set it runs and whether that has been read, and the eras a night on
 * it can be set in. Nothing of how it is run and no action.
 */
export interface PlayableServer {
  id: number;
  name: string;
  running: boolean;
  access: Access;
  activeMods: { id: number; name: string }[];
  /** The identity of what the server runs, Workshop mods and Creator DLCs; null when it runs nothing. */
  modSetHash?: string | null;
  dlcs?: string[];
  /** Whether the platform has a catalogue of what the server runs now; without one no mission can be made for it. */
  scanned?: boolean;
  setting?: ServerSetting;
}

/**
 * The servers a mission can be made for and a night played on. Whoever makes
 * missions may read it (an editor is not a server manager), so every picker of
 * a server asks this and not the servers page's list.
 */
export function fetchPlayableServers(): Promise<{ servers: PlayableServer[] }> {
  return apiFetch<{ servers: PlayableServer[] }>('/api/servers/playable');
}

export function serverAction(
  id: number,
  action: 'start' | 'stop' | 'restart' | 'link' | 'unlink',
): Promise<void> {
  return apiFetch<void>(`/api/servers/${id}/${action}`, { method: 'POST' });
}

export function createServer(input: ServerRequest): Promise<ServerView> {
  return apiFetch<ServerView>('/api/servers', { method: 'POST', body: input });
}

export function configureServer(id: number, input: ServerRequest): Promise<ServerView> {
  return apiFetch<ServerView>(`/api/servers/${id}`, { method: 'PUT', body: input });
}

export function deleteServer(id: number): Promise<void> {
  return apiFetch<void>(`/api/servers/${id}`, { method: 'DELETE' });
}

/**
 * How far the panel has got with one Workshop mod it was told to download.
 * `percent` and the bytes are null when the panel gives no figures (an older
 * panel): then only `state` says where the mod is.
 */
export interface ModProgress {
  id: number;
  name: string;
  state: 'ready' | 'downloading' | 'queued' | 'error' | 'unknown';
  percent: number | null;
  bytesFinished: number | null;
  bytesTotal: number | null;
}

/** The Workshop ids a 409 `mods_downloading` carries; empty for any other error (or an older platform's answer). */
export function downloadingIds(err: unknown): number[] {
  if (!(err instanceof ApiError) || err.code !== 'mods_downloading') return [];
  const ids = (err.body as { modIds?: unknown } | null)?.modIds;
  return Array.isArray(ids) ? ids.filter((id): id is number => typeof id === 'number') : [];
}

export function fetchModProgress(ids: number[]): Promise<ModProgress[]> {
  return apiFetch<ModProgress[]>(`/api/servers/mods/progress?ids=${ids.join(',')}`);
}

/**
 * A Workshop mod's preview as a small tile. The platform answers it from its
 * cache and 404 while it has none (not fetched yet, no preview on the
 * Workshop, or a mod no server and no scanned mod set carries).
 */
export const modImageUrl = (workshopId: number) => `/api/mods/${workshopId}/image`;

/** Installs or updates the bundled addon on the panel (admins). */
export function installAddon(): Promise<void> {
  return apiFetch<void>('/api/servers/addon/install', { method: 'POST' });
}

/** "2 h 5 min" since `startedAt`, or null when unknown or in the future. */
export function formatUptime(startedAt: string | null): string | null {
  if (!startedAt) return null;
  const ms = Date.now() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
}

/** The last lines of a server's log on the panel; a stopped server still answers with why it stopped. */
export interface LogTail {
  serverId: number;
  lines: number;
  tail: string[];
}

export const fetchServerLog = (id: number, lines = 200) =>
  apiFetch<LogTail>(`/api/servers/${id}/log?lines=${lines}`);

/** Leaves a managed or linked server running a published mission; 409 `server_has_players` while anybody is on it. */
export const switchServerMission = (id: number, folder: string) =>
  apiFetch<{ folder: string }>(`/api/servers/${id}/mission`, { method: 'POST', body: { folder } });

/** What a content scan started through a server answers with. */
export interface ServerScan {
  scanId: number;
  status: string;
  modSetHash: string;
  /** Why the scan could not start, when its status is FAILED (a mod the panel is still downloading, no budget…). */
  error?: string | null;
}

/** Scans what the server runs now, on a throwaway server of the platform's own; answers with the scan running or already done. */
export const scanServerContent = (id: number) =>
  apiFetch<ServerScan>(`/api/servers/${id}/scan`, { method: 'POST' });
